/**
 * Copy text to the clipboard with a non-secure-context fallback.
 *
 * `navigator.clipboard` is UNDEFINED on non-secure origins (plain HTTP, e.g.
 * local dev on `lvh.me:3000`) — the async Clipboard API requires a secure
 * context (HTTPS or localhost). Calling `navigator.clipboard.writeText`
 * unguarded throws "undefined is not an object". This helper prefers the async
 * API when present and falls back to the legacy `execCommand('copy')` via an
 * off-screen textarea, so copy works on HTTP too.
 *
 * @param {string} text
 * @returns {Promise<boolean>} true if the copy succeeded
 */
export async function copyToClipboard(text) {
  const value = String(text ?? '');

  if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (_) {
      // Permission denied / not focused / non-secure quirk — fall through to legacy.
    }
  }

  if (typeof document === 'undefined') return false;
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}
